import * as THREE from 'three';
import type { StyleConfig } from '../render/StyleConfig';

export type LightingPresetName =
  | 'desertHero'
  | 'hotDesertNoon'
  | 'electricSunset'
  | 'purpleDusk'
  | 'blueMoonNight'
  | 'neonStorm';

export interface LightingPreset {
  label: string;
  artDirection: StyleConfig['artDirection'];
  sky: Omit<StyleConfig['sky'], 'panoramaUrl'>;
  sunRig: StyleConfig['sunRig'];
  fog: StyleConfig['fog'];
  sfx: {
    bloom: StyleConfig['sfx']['bloom'];
    colorGrade: Pick<StyleConfig['sfx']['colorGrade'], 'exposure' | 'enabled' | 'saturation' | 'contrast' | 'gamma'>;
    vignette: Pick<StyleConfig['sfx']['vignette'], 'strength'>;
  };
}

export interface LightingSnapshot {
  master: StyleConfig['master'];
  overrides: StyleConfig['overrides'];
}

const PRESET_OVERRIDE_PATHS = [
  'sky.horizonHex',
  'sky.lowerHex',
  'sky.midHex',
  'sky.upperHex',
  'sky.zenithHex',
  'sky.oppositeHorizonHex',
  'sky.oppositeHorizonStrength',
  'sky.horizonGlowHex',
  'sky.horizonGlowIntensity',
  'sky.starIntensity',
  'sunRig.key.colorHex',
  'sunRig.key.intensity',
  'sunRig.fill.colorHex',
  'sunRig.fill.intensity',
  'sunRig.hemisphere.skyColorHex',
  'sunRig.hemisphere.groundColorHex',
  'sunRig.hemisphere.intensity',
  'sunRig.bounce.colorHex',
  'sunRig.bounce.intensity',
  'sunRig.rim.colorHex',
  'sunRig.rim.intensity',
  'sunRig.rim.cameraAnchored',
  'sunRig.rim.cameraSpaceDir',
  'sunRig.environmentIntensity',
  'fog.colorHex',
  'fog.density',
  'fog.followSky',
  'fog.inscatterStrength',
  'fog.inscatterPower',
  'fog.warmTintHex',
  'fog.coolTintHex',
  'derivedLighting.sunColorHex',
  'derivedLighting.sunIntensity',
  'derivedLighting.fillIntensity',
  'derivedLighting.hemiSkyHex',
  'derivedLighting.hemiGroundHex',
  'derivedLighting.hemiIntensity',
  'derivedLighting.environmentIntensity',
  'derivedLighting.fogColorHex',
  'derivedLighting.fogDensity',
  'derivedLighting.bounceColorHex',
  'derivedLighting.rimColorHex',
] as const;

const DEFAULT_VALUE_COMPOSITION: StyleConfig['artDirection']['valueComposition'] = {
  fgExposure: 1.0,
  mgExposure: 0.95,
  bgExposure: 1.15,
};

const DEFAULT_CAMERA_SPACE_RIM = { x: 0.7, y: 0.4, z: 0.6 };

function artDirection(config: Omit<StyleConfig['artDirection'], 'valueComposition'>): StyleConfig['artDirection'] {
  return { ...config, valueComposition: { ...DEFAULT_VALUE_COMPOSITION } };
}

function rim(config: Omit<StyleConfig['sunRig']['rim'], 'cameraAnchored' | 'cameraSpaceDir'>): StyleConfig['sunRig']['rim'] {
  return {
    ...config,
    cameraAnchored: true,
    cameraSpaceDir: { ...DEFAULT_CAMERA_SPACE_RIM },
  };
}

function fog(config: Pick<StyleConfig['fog'], 'colorHex' | 'density' | 'followSky'>): StyleConfig['fog'] {
  return {
    ...config,
    inscatterStrength: 0.6,
    inscatterPower: 6,
    warmTintHex: '#ffd9a6',
    coolTintHex: '#88a8c4',
  };
}

export const LIGHTING_PRESETS: Record<LightingPresetName, LightingPreset> = {
  desertHero: {
    label: 'Desert Hero',
    artDirection: artDirection({ preset: 'desertHero', drama: 0.68, warmth: 0.72, saturation: 0.72, contrast: 0.68, hazeGlow: 0.56 }),
    sky: {
      horizonHex: '#f4c18a',
      lowerHex: '#ff8f58',
      midHex: '#4fb8ee',
      upperHex: '#2b7fd4',
      zenithHex: '#0b70bf',
      oppositeHorizonHex: '#8be7ff',
      oppositeHorizonStrength: 0.22,
      horizonGlowHex: '#ffd06f',
      horizonGlowIntensity: 1.1,
      sunDiscScale: 1.18,
      sunHaloScale: 1.35,
      secondarySunDiscScale: 0.72,
      secondarySunHaloScale: 0.85,
      starIntensity: 0,
      sunDir: { x: 72, y: 22, z: 48 },
      secondarySunDir: { x: 120, y: 70, z: 80 },
      binary: { centerElevation: 30, centerAzimuth: 54, separationDeg: 18, tiltDeg: 14, distance: 100 },
    },
    sunRig: {
      key: { colorHex: '#ffcf86', intensity: 4.45, minElevationDeg: -35, elevationOffsetDeg: 0, azimuthOffsetDeg: 0, castShadow: true },
      fill: { colorHex: '#9ad7ff', intensity: 0.7, minElevationDeg: -35 },
      hemisphere: { skyColorHex: '#9fdcff', groundColorHex: '#5b4638', intensity: 0.5 },
      bounce: { colorHex: '#d77a42', intensity: 0.55, elevationDeg: -24, azimuthDeg: -28 },
      rim: rim({ colorHex: '#cfe6ff', intensity: 0.4, elevationDeg: 18, azimuthDeg: -135 }),
      environmentIntensity: 0.36,
    },
    fog: fog({ colorHex: '#e98a55', density: 0.0012, followSky: false }),
    sfx: {
      bloom: { threshold: 1.5, radius: 0.6, intensity: 0 },
      colorGrade: {
        exposure: 1.08,
        enabled: true,
        saturation: 1.2,
        contrast: 1.25,
        gamma: 1.06,
      },
      vignette: { strength: 0.24 },
    },
  },
  hotDesertNoon: {
    label: 'Hot Desert Noon',
    artDirection: artDirection({ preset: 'hotDesertNoon', drama: 0.42, warmth: 0.48, saturation: 0.64, contrast: 0.45, hazeGlow: 0.28 }),
    sky: {
      horizonHex: '#a9e7ff',
      lowerHex: '#8edfff',
      midHex: '#35aef0',
      upperHex: '#1688d8',
      zenithHex: '#0872c7',
      oppositeHorizonHex: '#fff0c0',
      oppositeHorizonStrength: 0.08,
      horizonGlowHex: '#fff1c8',
      horizonGlowIntensity: 0.38,
      sunDiscScale: 0.92,
      sunHaloScale: 0.72,
      secondarySunDiscScale: 0.52,
      secondarySunHaloScale: 0.42,
      starIntensity: 0,
      sunDir: { x: 48, y: 108, z: 42 },
      secondarySunDir: { x: -90, y: 80, z: -40 },
      binary: { centerElevation: 58, centerAzimuth: 25, separationDeg: 26, tiltDeg: -8, distance: 126 },
    },
    sunRig: {
      key: { colorHex: '#fff1c8', intensity: 3.55, minElevationDeg: -35, elevationOffsetDeg: 0, azimuthOffsetDeg: 0, castShadow: true },
      fill: { colorHex: '#b9e6ff', intensity: 0.45, minElevationDeg: -35 },
      hemisphere: { skyColorHex: '#bcecff', groundColorHex: '#6a5141', intensity: 0.56 },
      bounce: { colorHex: '#c9743e', intensity: 0.24, elevationDeg: -18, azimuthDeg: 0 },
      rim: rim({ colorHex: '#d9f0ff', intensity: 0.12, elevationDeg: 24, azimuthDeg: -120 }),
      environmentIntensity: 0.3,
    },
    fog: fog({ colorHex: '#b9e9ff', density: 0.0002, followSky: false }),
    sfx: {
      bloom: { threshold: 2.25, radius: 0.46, intensity: 0.62 },
      colorGrade: {
        exposure: 1.0,
        enabled: false,
        saturation: 1.05,
        contrast: 1.08,
        gamma: 1.04,
      },
      vignette: { strength: 0.16 },
    },
  },
  electricSunset: {
    label: 'Electric Sunset',
    artDirection: artDirection({ preset: 'electricSunset', drama: 0.9, warmth: 0.92, saturation: 0.86, contrast: 0.82, hazeGlow: 0.88 }),
    sky: {
      horizonHex: '#ffd46f',
      lowerHex: '#ff7a42',
      midHex: '#c85cff',
      upperHex: '#3852b9',
      zenithHex: '#121447',
      oppositeHorizonHex: '#22d8ff',
      oppositeHorizonStrength: 0.22,
      horizonGlowHex: '#ffd36e',
      horizonGlowIntensity: 1.65,
      sunDiscScale: 1.45,
      sunHaloScale: 1.95,
      secondarySunDiscScale: 0.75,
      secondarySunHaloScale: 1.15,
      starIntensity: 0,
      sunDir: { x: 100, y: 8, z: 18 },
      secondarySunDir: { x: 82, y: 14, z: 38 },
      binary: { centerElevation: 7, centerAzimuth: 74, separationDeg: 18, tiltDeg: 18, distance: 104 },
    },
    sunRig: {
      key: { colorHex: '#ffb16a', intensity: 5.2, minElevationDeg: -35, elevationOffsetDeg: 0, azimuthOffsetDeg: 0, castShadow: true },
      fill: { colorHex: '#a067ff', intensity: 0.74, minElevationDeg: -35 },
      hemisphere: { skyColorHex: '#755cff', groundColorHex: '#6b3f35', intensity: 0.42 },
      bounce: { colorHex: '#ff7748', intensity: 0.5, elevationDeg: -20, azimuthDeg: 10 },
      rim: rim({ colorHex: '#ffb7ef', intensity: 0.38, elevationDeg: 16, azimuthDeg: -145 }),
      environmentIntensity: 0.24,
    },
    fog: fog({ colorHex: '#ff9165', density: 0.0013, followSky: false }),
    sfx: {
      bloom: { threshold: 1.82, radius: 0.66, intensity: 1.08 },
      colorGrade: {
        exposure: 1.12,
        enabled: false,
        saturation: 1.18,
        contrast: 1.22,
        gamma: 1.08,
      },
      vignette: { strength: 0.3 },
    },
  },
  purpleDusk: {
    label: 'Purple Dusk',
    artDirection: artDirection({ preset: 'purpleDusk', drama: 0.78, warmth: 0.44, saturation: 0.82, contrast: 0.74, hazeGlow: 0.72 }),
    sky: {
      horizonHex: '#ffb48a',
      lowerHex: '#eb6ba4',
      midHex: '#8f54d9',
      upperHex: '#34429c',
      zenithHex: '#120f3d',
      oppositeHorizonHex: '#44d8ff',
      oppositeHorizonStrength: 0.16,
      horizonGlowHex: '#ff9d7a',
      horizonGlowIntensity: 0.95,
      sunDiscScale: 0.9,
      sunHaloScale: 1.5,
      secondarySunDiscScale: 0.66,
      secondarySunHaloScale: 1.1,
      starIntensity: 0.1,
      sunDir: { x: 94, y: -5, z: 24 },
      secondarySunDir: { x: 72, y: 8, z: 48 },
      binary: { centerElevation: 2, centerAzimuth: 66, separationDeg: 15, tiltDeg: -16, distance: 98 },
    },
    sunRig: {
      key: { colorHex: '#ff9a72', intensity: 3.4, minElevationDeg: -35, elevationOffsetDeg: 0, azimuthOffsetDeg: 0, castShadow: true },
      fill: { colorHex: '#8db8ff', intensity: 0.9, minElevationDeg: -35 },
      hemisphere: { skyColorHex: '#7b77ff', groundColorHex: '#432446', intensity: 0.38 },
      bounce: { colorHex: '#b75158', intensity: 0.34, elevationDeg: -24, azimuthDeg: -12 },
      rim: rim({ colorHex: '#efb4ff', intensity: 0.44, elevationDeg: 14, azimuthDeg: -132 }),
      environmentIntensity: 0.2,
    },
    fog: fog({ colorHex: '#b55c94', density: 0.0012, followSky: false }),
    sfx: {
      bloom: { threshold: 1.72, radius: 0.72, intensity: 1.16 },
      colorGrade: {
        exposure: 0.98,
        enabled: false,
        saturation: 1.24,
        contrast: 1.26,
        gamma: 1.1,
      },
      vignette: { strength: 0.33 },
    },
  },
  blueMoonNight: {
    label: 'Blue Moon Night',
    artDirection: artDirection({ preset: 'blueMoonNight', drama: 0.84, warmth: 0.16, saturation: 0.72, contrast: 0.78, hazeGlow: 0.36 }),
    sky: {
      horizonHex: '#1b5272',
      lowerHex: '#123a63',
      midHex: '#123c76',
      upperHex: '#0b2052',
      zenithHex: '#070d2a',
      oppositeHorizonHex: '#32164d',
      oppositeHorizonStrength: 0.16,
      horizonGlowHex: '#59a7ff',
      horizonGlowIntensity: 0.56,
      sunDiscScale: 1.15,
      sunHaloScale: 1.65,
      secondarySunDiscScale: 0.55,
      secondarySunHaloScale: 0.75,
      starIntensity: 0.72,
      sunDir: { x: 70, y: 10, z: -88 },
      secondarySunDir: { x: -120, y: 34, z: 64 },
      binary: { centerElevation: 18, centerAzimuth: 148, separationDeg: 44, tiltDeg: 10, distance: 114 },
    },
    sunRig: {
      key: { colorHex: '#8ad7ff', intensity: 1.3, minElevationDeg: -35, elevationOffsetDeg: 0, azimuthOffsetDeg: 0, castShadow: true },
      fill: { colorHex: '#ff7ad9', intensity: 0.72, minElevationDeg: -35 },
      hemisphere: { skyColorHex: '#1d5c94', groundColorHex: '#090815', intensity: 0.32 },
      bounce: { colorHex: '#23285d', intensity: 0.12, elevationDeg: -18, azimuthDeg: 0 },
      rim: rim({ colorHex: '#8fd4ff', intensity: 0.44, elevationDeg: 18, azimuthDeg: -138 }),
      environmentIntensity: 0.14,
    },
    fog: fog({ colorHex: '#153d62', density: 0.0009, followSky: false }),
    sfx: {
      bloom: { threshold: 1.45, radius: 0.78, intensity: 1.35 },
      colorGrade: {
        exposure: 0.78,
        enabled: false,
        saturation: 1.28,
        contrast: 1.24,
        gamma: 1.1,
      },
      vignette: { strength: 0.34 },
    },
  },
  neonStorm: {
    label: 'Neon Storm',
    artDirection: artDirection({ preset: 'neonStorm', drama: 0.94, warmth: 0.36, saturation: 0.94, contrast: 0.88, hazeGlow: 0.6 }),
    sky: {
      horizonHex: '#1fe0d0',
      lowerHex: '#264bb6',
      midHex: '#7928d9',
      upperHex: '#1a1f66',
      zenithHex: '#040824',
      oppositeHorizonHex: '#ff43b8',
      oppositeHorizonStrength: 0.28,
      horizonGlowHex: '#28ffe4',
      horizonGlowIntensity: 0.82,
      sunDiscScale: 0.65,
      sunHaloScale: 1.25,
      secondarySunDiscScale: 0.62,
      secondarySunHaloScale: 1.55,
      starIntensity: 0.42,
      sunDir: { x: 66, y: -12, z: -86 },
      secondarySunDir: { x: -96, y: 18, z: 54 },
      binary: { centerElevation: 4, centerAzimuth: 156, separationDeg: 58, tiltDeg: -22, distance: 110 },
    },
    sunRig: {
      key: { colorHex: '#45e8ff', intensity: 1.65, minElevationDeg: -35, elevationOffsetDeg: 0, azimuthOffsetDeg: 0, castShadow: true },
      fill: { colorHex: '#ff51d9', intensity: 1.1, minElevationDeg: -35 },
      hemisphere: { skyColorHex: '#254dff', groundColorHex: '#12041c', intensity: 0.34 },
      bounce: { colorHex: '#5426a8', intensity: 0.18, elevationDeg: -18, azimuthDeg: 18 },
      rim: rim({ colorHex: '#ff77ff', intensity: 0.62, elevationDeg: 12, azimuthDeg: -130 }),
      environmentIntensity: 0.18,
    },
    fog: fog({ colorHex: '#143b6b', density: 0.001, followSky: false }),
    sfx: {
      bloom: { threshold: 1.34, radius: 0.82, intensity: 1.48 },
      colorGrade: {
        exposure: 0.82,
        enabled: false,
        saturation: 1.42,
        contrast: 1.32,
        gamma: 1.12,
      },
      vignette: { strength: 0.38 },
    },
  },
};

export function applyLightingPreset(config: StyleConfig, name: LightingPresetName): LightingPreset {
  const preset = LIGHTING_PRESETS[name];
  Object.assign(config.artDirection, preset.artDirection);
  Object.assign(config.sky, preset.sky);
  Object.assign(config.sunRig.key, preset.sunRig.key);
  Object.assign(config.sunRig.fill, preset.sunRig.fill);
  Object.assign(config.sunRig.hemisphere, preset.sunRig.hemisphere);
  Object.assign(config.sunRig.bounce, preset.sunRig.bounce);
  Object.assign(config.sunRig.rim, preset.sunRig.rim);
  config.sunRig.environmentIntensity = preset.sunRig.environmentIntensity;
  Object.assign(config.fog, preset.fog);
  Object.assign(config.sfx.bloom, preset.sfx.bloom);
  Object.assign(config.sfx.colorGrade, preset.sfx.colorGrade);
  Object.assign(config.sfx.vignette, preset.sfx.vignette);
  const snapshot = lightingPresetSnapshot(preset);
  config.master = snapshot.master;
  config.overrides = snapshot.overrides;
  return preset;
}

export function lightingPresetSnapshot(preset: LightingPreset): LightingSnapshot {
  const primary = directionToAngles(preset.sky.sunDir);
  const secondary = directionToAngles(preset.sky.secondarySunDir);
  const config = {
    sky: preset.sky,
    sunRig: preset.sunRig,
    fog: preset.fog,
    sfx: preset.sfx,
    derivedLighting: derivedLightingOverridesFromPreset(preset),
  };
  const overrides: StyleConfig['overrides'] = {};
  for (const path of PRESET_OVERRIDE_PATHS) {
    const value = getPath(config, path);
    if (value !== undefined) overrides[path] = { enabled: true, value: clone(value) };
  }
  return {
    master: {
      sunElevationDeg: primary.elevation,
      sunAzimuthDeg: primary.azimuth,
      secondarySun: {
        enabled: true,
        relativeElevationDeg: secondary.elevation - primary.elevation,
        relativeAzimuthDeg: secondary.azimuth - primary.azimuth,
        intensity: preset.sunRig.fill.intensity,
      },
      atmosphere: {
        turbidity: Math.max(1, Math.min(10, 2 + preset.fog.density * 2200)),
        hazeBoost: Math.max(0, Math.min(2, preset.fog.density / 0.001)),
        warmthBias: preset.artDirection.warmth * 2 - 1,
      },
    },
    overrides,
  };
}

function derivedLightingOverridesFromPreset(preset: LightingPreset): Partial<Record<string, unknown>> {
  return {
    sunColorHex: preset.sunRig.key.colorHex,
    sunIntensity: preset.sunRig.key.intensity,
    fillIntensity: preset.sunRig.fill.intensity,
    hemiSkyHex: preset.sunRig.hemisphere.skyColorHex,
    hemiGroundHex: preset.sunRig.hemisphere.groundColorHex,
    hemiIntensity: preset.sunRig.hemisphere.intensity,
    environmentIntensity: preset.sunRig.environmentIntensity,
    fogColorHex: preset.fog.colorHex,
    fogDensity: preset.fog.density,
    bounceColorHex: preset.sunRig.bounce.colorHex,
    rimColorHex: preset.sunRig.rim.colorHex,
  };
}

export function applyBinarySunLayout(config: StyleConfig): void {
  const { binary } = config.sky;
  const tilt = THREE.MathUtils.degToRad(binary.tiltDeg);
  const azimuthOffset = Math.cos(tilt) * binary.separationDeg * 0.5;
  const elevationOffset = Math.sin(tilt) * binary.separationDeg * 0.5;
  anglesToDirection({
    elevation: binary.centerElevation + elevationOffset,
    azimuth: binary.centerAzimuth - azimuthOffset,
    distance: binary.distance,
  }, config.sky.sunDir);
  anglesToDirection({
    elevation: binary.centerElevation - elevationOffset,
    azimuth: binary.centerAzimuth + azimuthOffset,
    distance: binary.distance,
  }, config.sky.secondarySunDir);
}

export function directionToAngles(dir: { x: number; y: number; z: number }): {
  elevation: number;
  azimuth: number;
  distance: number;
} {
  const horizontal = Math.hypot(dir.x, dir.z);
  const distance = Math.max(1, Math.hypot(horizontal, dir.y));
  return {
    elevation: THREE.MathUtils.radToDeg(Math.atan2(dir.y, horizontal)),
    azimuth: THREE.MathUtils.radToDeg(Math.atan2(dir.x, dir.z)),
    distance,
  };
}

export function anglesToDirection(
  angles: { elevation: number; azimuth: number; distance: number },
  dir: { x: number; y: number; z: number },
): void {
  const elevation = THREE.MathUtils.degToRad(angles.elevation);
  const azimuth = THREE.MathUtils.degToRad(angles.azimuth);
  const horizontal = Math.cos(elevation) * angles.distance;
  dir.x = Math.sin(azimuth) * horizontal;
  dir.y = Math.sin(elevation) * angles.distance;
  dir.z = Math.cos(azimuth) * horizontal;
}

function getPath(source: unknown, path: string): unknown {
  let cursor = source as Record<string, unknown>;
  for (const part of path.split('.')) {
    if (!cursor || typeof cursor !== 'object') return undefined;
    cursor = cursor[part] as Record<string, unknown>;
  }
  return cursor;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
